import { Receipt } from '../types';
import { logger } from './logger';

// V produkčním prostředí (Docker) se bude používat relativní cesta, aby to fungovalo s reverzním proxy.
// Pro lokální vývoj to můžeme stále přepsat pomocí localStorage pro testování s BE na jiném portu.
export const getApiUrl = (): string => {
    try {
        // 1. Priorita: Manuální přepsání uživatelem pro ladění nebo specifická prostředí (jako AI Studio).
        const localApiUrl = localStorage.getItem('apiUrl');
        if (localApiUrl) {
            return localApiUrl;
        }

        const hostname = window.location.hostname;
        // 2. Automatická detekce pro lokální vývoj mimo AI Studio / Docker.
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return 'http://localhost:8080';
        }

        // 3. Výchozí hodnota pro produkci (Docker) a AI Studio (kde je nutné manuální nastavení).
        // Prázdný řetězec znamená, že se použijí relativní cesty (např. /api/login).
        // Toto je nejbezpečnější výchozí hodnota, která nezpůsobuje chyby smíšeného obsahu (Mixed Content).
        return '';
    } catch (e) {
        // V případě bezpečnostních chyb s localStorage (např. v sandboxed iframes) se vrátí relativní cesta.
        return '';
    }
};


export const constructFileUrl = (relativeUrl?: string): string | null => {
    if (!relativeUrl) return null;

    // Pro externí URL (např. z CSV importu) se nic nemění.
    if (relativeUrl.startsWith('http')) {
        return relativeUrl;
    }

    const apiUrl = getApiUrl();
    // Nově pouze spojíme základní URL a relativní cestu, bez tokenu.
    const fullUrl = `${apiUrl}${relativeUrl}`;
    return fullUrl;
};


export const normalizePaymentMethod = (method?: string): string => {
    const defaultMethod = "Neznámá";
    if (!method) return defaultMethod;
    const lowerMethod = method.toLowerCase();

    if (lowerMethod.includes('karto') || lowerMethod.includes('card')) return "Kartou";
    if (lowerMethod.includes('hotov') || lowerMethod.includes('cash')) return "Hotově";
    if (lowerMethod.includes('převod') || lowerMethod.includes('transfer')) return "Převodem";
    
    return method.charAt(0).toUpperCase() + method.slice(1) || defaultMethod;
};

const formatPascalCase = (str: string = '') => {
    return str
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .split(' ')
        .filter(word => word)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
};

export const generateReceiptFileName = (data: {
    receiptDate?: string;
    businessName?: string;
    category: { name: string };
    amount?: number | null;
    currency: { name: string };
}): string => {
    logger.debug('[generateReceiptFileName] Input data for filename generation:', data);
    const date = data.receiptDate ? new Date(data.receiptDate) : null;
    const datePart = date
        ? `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
        : 'DatumNeni';

    const businessPart = formatPascalCase(data.businessName || 'NeznamyPodnik');

    const categoryPart = data.category ? data.category.name.toLowerCase() : 'kategorie';

    const amountPart = (typeof data.amount === 'number' && data.currency?.name)
        ? `${Number(data.amount).toFixed(2)}${data.currency.name}`
        : 'CastkaNeni';
        
    const finalName = `${datePart} - ${businessPart} - ${categoryPart} - ${amountPart}`;
    logger.debug(`[generateReceiptFileName] Generated filename: "${finalName}"`);
    return finalName;
};


export const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let currentField = '';
    let inQuotedField = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (inQuotedField) {
            if (char === '"') {
                if (i < line.length - 1 && line[i + 1] === '"') {
                    currentField += '"';
                    i++; 
                } else {
                    inQuotedField = false;
                }
            } else {
                currentField += char;
            }
        } else {
            if (char === '"') {
                inQuotedField = true;
            } else if (char === ';') {
                result.push(currentField);
                currentField = '';
            } else {
                currentField += char;
            }
        }
    }
    result.push(currentField);
    return result;
};

export const getNestedValue = (obj: any, path: string): any => {
    if (!path) return obj;
    return path.split('.').reduce((acc, part) => acc && acc[part] !== undefined ? acc[part] : null, obj);
};

export const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
        const repairedDateString = /^\d{8}$/.test(dateString)
            ? `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`
            : dateString;
        const date = new Date(repairedDateString);
        if (isNaN(date.getTime())) return dateString; // Pokud je stále neplatné, vrať originál
        return date.toLocaleDateString('cs-CZ', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
        return dateString;
    }
};

export const formatDateForInput = (dateString?: string): string => {
    if (!dateString || typeof dateString !== 'string') return '';
    
    // Try to match YYYY-MM-DD at the start of the string
    const match = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
        return match[1];
    }

    // Fallback for YYYYMMDD
    if (/^\d{8}$/.test(dateString)) {
        return `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`;
    }
    
    return ''; // Return empty if format is not recognized
};

export const getContrastColor = (hexcolor?: string): string => {
    if (!hexcolor) return '#000000';
    if (hexcolor.slice(0, 1) === '#') {
        hexcolor = hexcolor.slice(1);
    }
    if (hexcolor.length === 3) {
        hexcolor = hexcolor.split('').map(char => char + char).join('');
    }
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '#000000' : '#FFFFFF';
};

export const dataURLtoBlob = (dataurl: string): Blob => {
    const arr = dataurl.split(',');
    if (arr.length < 2) {
        throw new Error('Invalid data URL');
    }
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
        throw new Error('Could not determine MIME type from data URL');
    }
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
};